import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getEntry,
  getEntryChipHistory,
  getEntryEventHistory,
  getEntrySeasonHistory,
} from "core-integration/src/store/entries/reducers";
import {
  fetchEntryHistory,
  fetchEntrySummary,
} from "core-integration/src/store/entries/thunks";
import { formatRawAsLocal } from "core-integration/src/utils/datetime";
import { integerToMoney } from "core-integration/src/utils/money";
import { mean } from "core-integration/src/utils/number";
import Link from "plos/src/components/links/Link";
import PageTitle from "plos/src/components/PageTitle";
import Subheading from "plos/src/components/Subheading";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { Cell, Column, Row, Table } from "plos/src/components/Table";
import { InfoText } from "plos/src/components/tooltips/InfoText";
import {
  contentMain,
  historyMain,
  historySidebar,
  leftSidebarLayout,
} from "plos/src/layouts";
import { scrollContainer } from "plos/src/styles/utils.css";
import { ReactNode, useEffect } from "react";
import { TableBody, TableHeader } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { getChipName } from "../utils/chips";
import { getShortNameFromId } from "../utils/events";
import EntryInfo from "./EntryInfo";
import { HelmetHead } from "./HelmetHead";
import Movement from "./leagues/Movement";

const EntryHistory = () => {
  const { t } = useTranslation("entryHistory");
  const dispatch = useDispatch<ThunkDispatch>();
  const { entryId } = useParams();

  const entryIdNumber = Number(entryId);
  const chipHistory = useSelector((state: RootState) =>
    getEntryChipHistory(state, entryIdNumber),
  );
  const entry = useSelector((state: RootState) =>
    getEntry(state, entryIdNumber),
  );
  const eventHistory = useSelector((state: RootState) =>
    [...getEntryEventHistory(state, entryIdNumber)].reverse(),
  );
  const seasonHistory = useSelector((state: RootState) =>
    getEntrySeasonHistory(state, entryIdNumber),
  );
  const reversedSeasonHistory = [...seasonHistory].reverse();

  const careerAvgPoints = mean(seasonHistory.map((sh) => sh.total_points));
  const careerAvgRank = mean(seasonHistory.map((sh) => sh.rank));
  const careerAvgRankPercentage = mean(
    seasonHistory
      .map((sh) => sh.rank_percentage)
      .filter((v): v is string => v != null)
      .map((v) => parseFloat(v)),
  );

  useEffect(() => {
    dispatch(fetchEntryHistory(entryIdNumber));
    dispatch(fetchEntrySummary(entryIdNumber));
  }, [dispatch, entryIdNumber]);

  const movementFromIndex = (index: number): ReactNode => {
    const thisRank = eventHistory[index].overall_rank;
    const lastRank =
      index === eventHistory.length - 1
        ? null
        : eventHistory[index + 1].overall_rank;
    return <Movement lastRank={lastRank} rank={thisRank} />;
  };

  if (!entry) {
    return null;
  }

  return (
    <>
      <HelmetHead
        title={t(
          "metadata.entryHistory.title ",
          "Fantasy Football Gameweek History | Fantasy Premier League",
        )}
        description={t(
          "metadata.entryHistory.description",
          "To view the Gameweek history, along with the status of your Chips, visit the official website of the Premier League.",
        )}
      />
      <div className={leftSidebarLayout}>
        <div className={historyMain}>
          <SurfaceContainer>
            <div className={contentMain}>
              <PageTitle
                title={t("entryHistoryTable.title", "Entry History")}
              />
              <Subheading>
                {t("entryHistoryTable.subHeading.thisSeason", "This Season")}
              </Subheading>
              <div className={scrollContainer}>
                <Table
                  aria-label={
                    t("entryHistoryTable.label", "Entry History") as string
                  }
                >
                  <TableHeader>
                    <Column isRowHeader>
                      <InfoText
                        label={t(
                          "entryHistoryTable.gameweek.label",
                          "Gameweek",
                        )}
                      >
                        {t("entryHistoryTable.gameweek.abbreviation", "GW")}
                      </InfoText>
                    </Column>
                    <Column align="end">
                      <InfoText
                        label={t(
                          "entryHistoryTable.overallRank.label",
                          "Overall Rank",
                        )}
                      >
                        {t("entryHistoryTable.overallRank.abbreviation", "OR")}
                      </InfoText>
                    </Column>
                    <Column align="end">
                      <InfoText
                        label={t(
                          "entryHistoryTable.movement.label",
                          "Movement",
                        )}
                      >
                        #
                      </InfoText>
                    </Column>
                    <Column align="end">
                      <InfoText
                        label={t(
                          "entryHistoryTable.overallRankPercentage.label",
                          "Your overall rank as a percentage of all teams this season.",
                        )}
                      >
                        {t(
                          "entryHistoryTable.overallRankPercentage.abbreviation",
                          "% Rank",
                        )}
                      </InfoText>
                    </Column>
                    <Column align="end">
                      <InfoText
                        label={t(
                          "entryHistoryTable.overallPoints.label",
                          "Overall Points",
                        )}
                      >
                        {t(
                          "entryHistoryTable.overallPoints.abbreviation",
                          "OP",
                        )}
                      </InfoText>
                    </Column>
                    <Column align="end">
                      <InfoText
                        label={t(
                          "entryHistoryTable.gameweekRank.label",
                          "Gameweek Rank",
                        )}
                      >
                        {t(
                          "entryHistoryTable.gameweekRank.abbreviation",
                          "GWR",
                        )}
                      </InfoText>
                    </Column>
                    <Column align="end">
                      <InfoText
                        label={t(
                          "entryHistoryTable.gameweekPoints.label",
                          "Gameweek Points",
                        )}
                      >
                        {t(
                          "entryHistoryTable.gameweekPoints.abbreviation",
                          "GWP",
                        )}
                      </InfoText>
                    </Column>
                    <Column align="end">
                      <InfoText
                        label={t(
                          "entryHistoryTable.pointsOnBench.label",
                          "Points on Bench",
                        )}
                      >
                        {t(
                          "entryHistoryTable.pointsOnBench.abbreviation",
                          "PB",
                        )}
                      </InfoText>
                    </Column>
                    <Column align="end">
                      <InfoText
                        label={t(
                          "entryHistoryTable.transfersMade.label",
                          "Transfers Made",
                        )}
                      >
                        {t(
                          "entryHistoryTable.transfersMade.abbreviation",
                          "TM",
                        )}
                      </InfoText>
                    </Column>
                    <Column align="end">
                      <InfoText
                        label={t(
                          "entryHistoryTable.transfersCost.label",
                          "Transfers Cost",
                        )}
                      >
                        {t(
                          "entryHistoryTable.transfersCost.abbreviation",
                          "TC",
                        )}
                      </InfoText>
                    </Column>
                    <Column align="end">
                      <InfoText
                        label={t(
                          "entryHistoryTable.squadValue.label",
                          "Squad Value",
                        )}
                      >
                        {t("entryHistoryTable.squadValue.abbreviation", "£m")}
                      </InfoText>
                    </Column>
                  </TableHeader>
                  <TableBody
                    renderEmptyState={() =>
                      t(
                        "entryHistoryTable.noGameweekHistory",
                        "No Gameweek history.",
                      )
                    }
                  >
                    {[...eventHistory].map((eh, i) => (
                      <Row key={eh.event}>
                        <Cell>
                          <Link to={`/entry/${entry.id}/event/${eh.event}`}>
                            {getShortNameFromId(eh.event)}
                          </Link>
                        </Cell>
                        <Cell align="end">
                          {eh.overall_rank
                            ? eh.overall_rank.toLocaleString()
                            : "-"}
                        </Cell>
                        <Cell align="end">{movementFromIndex(i)}</Cell>
                        <Cell align="end">
                          {eh.overall_rank_percentage == null
                            ? "-"
                            : `${eh.overall_rank_percentage}%`}
                        </Cell>
                        <Cell align="end">{eh.total_points}</Cell>
                        <Cell align="end">
                          {eh.rank ? eh.rank.toLocaleString() : "-"}
                        </Cell>
                        <Cell align="end">{eh.points.toLocaleString()}</Cell>
                        <Cell align="end">{eh.points_on_bench}</Cell>
                        <Cell align="end">{eh.event_transfers}</Cell>
                        <Cell align="end">{eh.event_transfers_cost}</Cell>
                        <Cell align="end">{integerToMoney(eh.value, 10)}</Cell>
                      </Row>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Subheading>
                {t("chipsHistoryTable.subHeading", "Chips")}
              </Subheading>
              <Table
                aria-label={
                  t("chipsHistoryTable.label", "Chips History Table") as string
                }
              >
                <TableHeader>
                  <Column isRowHeader>
                    {t("chipsHistoryTable.date", "Date")}
                  </Column>
                  <Column>{t("chipsHistoryTable.name", "Name")}</Column>
                  <Column>{t("chipsHistoryTable.active", "Active")}</Column>
                </TableHeader>
                <TableBody
                  renderEmptyState={() =>
                    t("chipsHistoryTable.noChipHistory", "No chip history.")
                  }
                >
                  {chipHistory.map((ch) => (
                    <Row key={ch.event}>
                      <Cell>{formatRawAsLocal(ch.time)}</Cell>
                      <Cell>{getChipName(t, ch.name)}</Cell>
                      <Cell>
                        <Link to={`/entry/${entry.id}/event/${ch.event}`}>
                          {getShortNameFromId(ch.event)}
                        </Link>
                      </Cell>
                    </Row>
                  ))}
                </TableBody>
              </Table>
              <Subheading>
                {t("seasonHistoryTable.subHeading", "Previous Seasons")}
              </Subheading>

              <Table
                aria-label={
                  t(
                    "seasonHistoryTable.label",
                    "Season History Table",
                  ) as string
                }
              >
                <TableHeader>
                  <Column isRowHeader>
                    {t("seasonHistoryTable.season", "Season")}
                  </Column>
                  <Column align="end">
                    {t("seasonHistoryTable.points", "Points")}
                  </Column>
                  <Column align="end">
                    {t("seasonHistoryTable.rank", "Rank")}
                  </Column>
                  <Column align="end">
                    <InfoText
                      label={t(
                        "seasonHistoryTable.finishPercentage.label",
                        "Your final rank as a percentage of all teams that season.",
                      )}
                    >
                      {t(
                        "seasonHistoryTable.finishPercentage.abbreviation",
                        "% Finish",
                      )}
                    </InfoText>
                  </Column>
                </TableHeader>
                <TableBody
                  renderEmptyState={() =>
                    t(
                      "seasonHistoryTable.noSeasonHistory",
                      "No season history.",
                    )
                  }
                >
                  {reversedSeasonHistory.map((sh) => (
                    <Row key={sh.season_name}>
                      <Cell>{sh.season_name}</Cell>
                      <Cell align="end">
                        {sh.total_points.toLocaleString()}
                      </Cell>
                      <Cell align="end">{sh.rank.toLocaleString()}</Cell>
                      <Cell align="end">
                        {sh.rank_percentage == null
                          ? "-"
                          : `${sh.rank_percentage}%`}
                      </Cell>
                    </Row>
                  ))}
                  {seasonHistory.length > 0 && (
                    <Row key="career-average">
                      <Cell>
                        {t(
                          "seasonHistoryTable.careerAverage",
                          "Career Average",
                        )}
                      </Cell>
                      <Cell align="end">
                        {careerAvgPoints == null
                          ? "-"
                          : Math.round(careerAvgPoints).toLocaleString()}
                      </Cell>
                      <Cell align="end">
                        {careerAvgRank == null
                          ? "-"
                          : Math.round(careerAvgRank).toLocaleString()}
                      </Cell>
                      <Cell align="end">
                        {careerAvgRankPercentage == null
                          ? "-"
                          : careerAvgRankPercentage < 1
                            ? `${careerAvgRankPercentage.toFixed(1)}%`
                            : `${Math.round(careerAvgRankPercentage)}%`}
                      </Cell>
                    </Row>
                  )}
                </TableBody>
              </Table>
            </div>
          </SurfaceContainer>
        </div>
        <div className={historySidebar}>
          <SurfaceContainer>
            <EntryInfo entryId={entry.id} />
          </SurfaceContainer>
        </div>
      </div>
    </>
  );
};

export default EntryHistory;
